<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
    </div>

    <!-- No lock yet — generate -->
    <div v-else-if="!lock" class="text-center py-12">
      <h3 class="text-lg font-semibold font-heading text-gray-900 dark:text-gray-100 mb-2">
        Heading &amp; Meta Structure
      </h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Generate AI options for title tag, meta description, H1, and heading structure based on competitor analysis.
      </p>
      <button @click="handleGenerate" :disabled="generating" class="btn btn-primary">
        {{ generating ? 'Generating...' : 'Generate Options' }}
      </button>
    </div>

    <!-- Generating -->
    <div v-else-if="lock.status === 'pending' || lock.status === 'generating'" class="text-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4" />
      <p class="text-sm text-gray-600 dark:text-gray-400">
        {{ progressMessage || 'Generating heading & meta options...' }}
      </p>
    </div>

    <!-- Failed -->
    <div v-else-if="lock.status === 'failed'" class="text-center py-12">
      <p class="text-sm text-red-600 dark:text-red-400 mb-4">
        {{ lock.error_message || 'Generation failed. Please try again.' }}
      </p>
      <button @click="handleGenerate" class="btn btn-primary">
        Retry
      </button>
    </div>

    <!-- Ready / Completed — wizard steps -->
    <div v-else class="space-y-6">
      <!-- Step Progress Bar -->
      <div class="flex items-center justify-between px-4">
        <div v-for="(step, i) in steps" :key="step.key" class="flex items-center" :class="{ 'flex-1': i < steps.length - 1 }">
          <div class="flex flex-col items-center">
            <div
              :class="[
                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors',
                stepDotClass(step.key)
              ]">
              <span v-if="stepStatus(step.key) === 'locked'" class="text-white">&#10003;</span>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <span class="text-xs mt-1 whitespace-nowrap" :class="stepLabelClass(step.key)">
              {{ step.label }}
            </span>
          </div>
          <div v-if="i < steps.length - 1" class="flex-1 h-0.5 mx-2 mt-[-12px]" :class="stepLineClass(step.key)" />
        </div>
      </div>

      <!-- Error -->
      <div v-if="error" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4">
        <p class="text-sm text-red-700 dark:text-red-400">{{ error }}</p>
      </div>

      <!-- Locked steps summaries -->
      <div v-for="step in lockedSteps" :key="'locked-' + step" class="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/10 p-4">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <span class="text-xs font-medium text-primary-700 dark:text-primary-400 uppercase tracking-wide">
              {{ stepLabel(step) }} — Locked
            </span>
            <p class="text-sm text-gray-900 dark:text-gray-100 mt-1">{{ lockedValue(step) }}</p>
          </div>
          <button @click="handleUnlock(step)" class="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1">
            Unlock
          </button>
        </div>
      </div>

      <!-- Step 1: Title Tag -->
      <div v-if="lock.current_step === 'title_tag'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 1: Choose Title Tag
        </h3>

        <!-- Competitor patterns -->
        <div v-if="lock.competitor_title_patterns && lock.competitor_title_patterns.length" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Competitor Title Patterns</p>
          <div class="space-y-1">
            <p v-for="(title, i) in lock.competitor_title_patterns" :key="i" class="text-xs text-gray-600 dark:text-gray-400">
              {{ title }}
            </p>
          </div>
        </div>

        <!-- AI Options -->
        <div class="space-y-3">
          <div
            v-for="(option, i) in (lock.title_tag_options || [])"
            :key="i"
            @click="selectedTitle = i"
            :class="[
              'rounded-xl border-2 p-4 cursor-pointer transition-colors',
              selectedTitle === i
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            ]">
            <div class="flex items-start justify-between">
              <p class="text-sm text-gray-900 dark:text-gray-100 font-medium flex-1">{{ option.text }}</p>
              <div class="flex items-center gap-2 ml-3 shrink-0">
                <span :class="charCountClass(option.text, 60)" class="text-xs px-2 py-0.5 rounded-lg font-medium">
                  {{ (option.text || '').length }} chars
                </span>
              </div>
            </div>
            <div class="flex items-center gap-2 mt-2">
              <span v-if="option.keyword_position" class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-0.5">
                Keyword position: {{ option.keyword_position }}
              </span>
              <span v-if="option.source" class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-0.5">
                {{ option.source }}
              </span>
            </div>
          </div>
        </div>

        <!-- Custom input -->
        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Or enter custom title:</label>
          <input
            v-model="customTitle"
            @focus="selectedTitle = null"
            type="text"
            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter custom title tag...">
          <div class="flex justify-end">
            <span :class="charCountClass(customTitle, 60)" class="text-xs px-2 py-0.5 rounded-lg font-medium">
              {{ customTitle.length }} / 60
            </span>
          </div>
        </div>

        <div class="flex justify-end">
          <button @click="handleLockTitle" :disabled="locking" class="btn btn-primary">
            {{ locking ? 'Locking...' : 'Lock Title Tag' }}
          </button>
        </div>
      </div>

      <!-- Step 2: Meta Description -->
      <div v-if="lock.current_step === 'meta_description'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 2: Choose Meta Description
        </h3>

        <div class="space-y-3">
          <div
            v-for="(option, i) in (lock.meta_description_options || [])"
            :key="i"
            @click="selectedMeta = i"
            :class="[
              'rounded-xl border-2 p-4 cursor-pointer transition-colors',
              selectedMeta === i
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            ]">
            <p class="text-sm text-gray-900 dark:text-gray-100">{{ option.text }}</p>
            <div class="flex items-center gap-2 mt-2">
              <span :class="charCountClass(option.text, 155)" class="text-xs px-2 py-0.5 rounded-lg font-medium">
                {{ (option.text || '').length }} chars
              </span>
              <span v-if="option.has_keyword" class="text-xs text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 rounded-lg px-2 py-0.5">
                Has keyword
              </span>
              <span v-if="option.has_cta" class="text-xs text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 rounded-lg px-2 py-0.5">
                Has CTA
              </span>
            </div>
          </div>
        </div>

        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Or enter custom description:</label>
          <textarea
            v-model="customMeta"
            @focus="selectedMeta = null"
            rows="3"
            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter custom meta description..." />
          <div class="flex justify-end">
            <span :class="charCountClass(customMeta, 155)" class="text-xs px-2 py-0.5 rounded-lg font-medium">
              {{ customMeta.length }} / 155
            </span>
          </div>
        </div>

        <div class="flex justify-end">
          <button @click="handleLockMeta" :disabled="locking" class="btn btn-primary">
            {{ locking ? 'Locking...' : 'Lock Meta Description' }}
          </button>
        </div>
      </div>

      <!-- Step 3: H1 -->
      <div v-if="lock.current_step === 'h1'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 3: Choose H1
        </h3>

        <div class="space-y-3">
          <div
            v-for="(option, i) in (lock.h1_options || [])"
            :key="i"
            @click="selectedH1 = i"
            :class="[
              'rounded-xl border-2 p-4 cursor-pointer transition-colors',
              selectedH1 === i
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            ]">
            <p class="text-sm text-gray-900 dark:text-gray-100 font-medium">{{ option.text }}</p>
            <div class="flex items-center gap-2 mt-2">
              <span v-if="option.formula" class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-0.5">
                {{ option.formula }}
              </span>
              <span v-if="option.source" class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-0.5">
                {{ option.source }}
              </span>
            </div>
          </div>
        </div>

        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Or enter custom H1:</label>
          <input
            v-model="customH1"
            @focus="selectedH1 = null"
            type="text"
            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter custom H1...">
        </div>

        <!-- Keyword warning for custom H1 -->
        <div v-if="customH1 && !customH1ContainsKeyword" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
          <p class="text-sm text-amber-700 dark:text-amber-400">
            Warning: Your custom H1 does not appear to contain the target keyword.
          </p>
        </div>

        <div class="flex justify-end">
          <button @click="handleLockH1" :disabled="locking" class="btn btn-primary">
            {{ locking ? 'Locking...' : 'Lock H1' }}
          </button>
        </div>
      </div>

      <!-- Step 4: Heading Structure -->
      <div v-if="lock.current_step === 'heading_structure'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 4: Heading Structure
        </h3>

        <!-- Heading tree editor -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-2">
          <div
            v-for="(heading, i) in editingHeadings"
            :key="i"
            class="flex items-center gap-2"
            :style="{ paddingLeft: `${(heading.level - 2) * 24}px` }">
            <!-- Level badge -->
            <span :class="headingLevelClass(heading.level)" class="text-xs font-bold px-2 py-0.5 rounded-lg shrink-0">
              H{{ heading.level }}
            </span>

            <!-- Editable text -->
            <input
              v-model="heading.text"
              type="text"
              class="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              :placeholder="`H${heading.level} heading text...`">

            <!-- Keywords pills -->
            <div v-if="heading.keywords && heading.keywords.length" class="flex items-center gap-1 shrink-0">
              <span
                v-for="(kw, ki) in heading.keywords"
                :key="ki"
                class="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg px-1.5 py-0.5">
                {{ kw }}
              </span>
            </div>

            <!-- Data source tag -->
            <span v-if="heading.source" class="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-lg px-1.5 py-0.5 shrink-0">
              {{ formatSource(heading.source) }}
            </span>

            <!-- Controls -->
            <div class="flex items-center gap-1 shrink-0">
              <button
                v-if="heading.level > 2"
                @click="outdentHeading(i)"
                class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Outdent">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7" /></svg>
              </button>
              <button
                v-if="heading.level < 4"
                @click="indentHeading(i)"
                class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Indent">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7" /></svg>
              </button>
              <button
                @click="removeHeading(i)"
                class="p-1 text-gray-400 hover:text-red-500"
                title="Remove">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>

          <!-- Add heading button -->
          <button @click="addHeading" class="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1 mt-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
            Add Heading
          </button>
        </div>

        <!-- Nesting validation -->
        <div v-if="nestingWarning" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
          <p class="text-sm text-amber-700 dark:text-amber-400">{{ nestingWarning }}</p>
        </div>

        <div class="flex justify-end">
          <button @click="handleLockHeadingStructure" :disabled="locking" class="btn btn-primary">
            {{ locking ? 'Locking...' : 'Lock Heading Structure' }}
          </button>
        </div>
      </div>

      <!-- Step 5: Keyword Verification -->
      <div v-if="lock.current_step === 'keyword_verification'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 5: Keyword Verification
        </h3>

        <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50">
            <span class="font-medium text-gray-900 dark:text-gray-100">Keyword Coverage</span>
            <span class="text-sm text-gray-500 dark:text-gray-400">
              {{ lock.verification_summary?.covered || 0 }} of {{ lock.verification_summary?.total || 0 }} keywords covered
            </span>
          </div>
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th class="px-4 py-2">Keyword</th>
                <th class="px-4 py-2">Status</th>
                <th class="px-4 py-2">In Headings</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(kv, i) in (lock.heading_keyword_verification || [])"
                :key="i"
                :class="kv.present ? 'bg-primary-50 dark:bg-primary-900/10' : 'bg-red-50 dark:bg-red-900/10'"
                class="border-t border-gray-100 dark:border-gray-800">
                <td class="px-4 py-2 text-gray-900 dark:text-gray-100">{{ kv.keyword }}</td>
                <td class="px-4 py-2">
                  <span v-if="kv.present" class="text-green-600 dark:text-green-400 text-xs font-medium">Present</span>
                  <span v-else class="text-red-600 dark:text-red-400 text-xs font-medium">Gap</span>
                </td>
                <td class="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
                  {{ (kv.found_in || []).join(', ') || '---' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex justify-end gap-3">
          <button @click="handleUnlock('heading_structure')" class="btn btn-secondary btn-sm">
            Go Back to Fix
          </button>
          <button @click="handleAcceptVerification" :disabled="locking" class="btn btn-primary">
            {{ locking ? 'Continuing...' : 'Accept & Continue' }}
          </button>
        </div>
      </div>

      <!-- Step 6: Target Lock -->
      <div v-if="lock.current_step === 'target_lock'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 6: Select & Confirm Targets
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Select which keywords to target in the content. Uncheck any that aren't relevant to this page.
        </p>

        <!-- Warning if all identical -->
        <div v-if="allTargetsIdentical" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
          <p class="text-sm text-amber-700 dark:text-amber-400">
            Warning: all targets are identical. Consider reviewing individual targets before locking.
          </p>
        </div>

        <!-- Selection summary -->
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600 dark:text-gray-400">
            {{ selectedKeywords.size }} of {{ (lock.locked_targets || []).length }} keywords selected
          </span>
          <div class="flex gap-2">
            <button @click="selectAllKeywords" class="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline">
              Select All
            </button>
            <span class="text-gray-300 dark:text-gray-600">|</span>
            <button @click="deselectAllKeywords" class="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline">
              Deselect All
            </button>
          </div>
        </div>

        <!-- Target table -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                <th class="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    :checked="allKeywordsSelected"
                    :indeterminate="someKeywordsSelected && !allKeywordsSelected"
                    @change="toggleAllKeywords"
                    class="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500" />
                </th>
                <th class="px-4 py-2">Keyword</th>
                <th class="px-4 py-2">Volume</th>
                <th class="px-4 py-2">Tier</th>
                <th v-for="comp in targetCompetitorColumns" :key="comp" class="px-4 py-2">{{ comp }}</th>
                <th class="px-4 py-2">Raw Avg</th>
                <th class="px-4 py-2">Cleaned Avg</th>
                <th class="px-4 py-2">Target</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(target, i) in sortedTargets"
                :key="i"
                :class="[
                  'border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50',
                  !selectedKeywords.has(target.keyword) && 'opacity-40'
                ]"
                @click="toggleKeyword(target.keyword)">
                <td class="px-3 py-2">
                  <input
                    type="checkbox"
                    :checked="selectedKeywords.has(target.keyword)"
                    @click.stop="toggleKeyword(target.keyword)"
                    class="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500" />
                </td>
                <td class="px-4 py-2 text-gray-900 dark:text-gray-100">{{ target.keyword }}</td>
                <td class="px-4 py-2 text-gray-600 dark:text-gray-400">{{ formatVolume(target.search_volume) }}</td>
                <td class="px-4 py-2">
                  <span :class="tierClass(target.tier)" class="text-xs font-medium px-2 py-0.5 rounded-lg">
                    {{ target.tier }}
                  </span>
                </td>
                <td v-for="comp in targetCompetitorColumns" :key="comp" class="px-4 py-2 text-gray-600 dark:text-gray-400">
                  {{ target.competitor_counts?.[comp] ?? '---' }}
                </td>
                <td class="px-4 py-2 text-gray-600 dark:text-gray-400">{{ target.raw_avg ?? '---' }}</td>
                <td class="px-4 py-2 text-gray-600 dark:text-gray-400">{{ target.cleaned_avg ?? '---' }}</td>
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">{{ target.target }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex items-center justify-between">
          <p v-if="selectedKeywords.size === 0" class="text-sm text-red-600 dark:text-red-400">
            Select at least one keyword to continue.
          </p>
          <div v-else></div>
          <button @click="handleLockTargets" :disabled="locking || selectedKeywords.size === 0" class="btn btn-primary">
            {{ locking ? 'Locking...' : `Confirm ${selectedKeywords.size} Targets & Lock` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import consumer from '~/channels/consumer'
import {
  generateHeadingMetaOptions,
  getHeadingMetaLock,
  lockTitleTag,
  lockMetaDescription,
  lockH1,
  lockHeadingStructure,
  verifyHeadingKeywords,
  lockTargets as lockTargetsApi,
  unlockStep
} from '~/javascript/api/heading_meta_lock.js'

const props = defineProps({
  pageId: { type: String, required: true }
})

const emit = defineEmits(['approved'])

let subscription = null
let pollTimer = null

const lock = ref(null)
const loading = ref(true)
const generating = ref(false)
const locking = ref(false)
const error = ref(null)
const progressMessage = ref('')
const selectedTitle = ref(null)
const customTitle = ref('')
const selectedMeta = ref(null)
const customMeta = ref('')
const selectedH1 = ref(null)
const customH1 = ref('')
const editingHeadings = ref([])
const selectedKeywords = ref(new Set())

const steps = [
  { key: 'title_tag', label: 'Title Tag' },
  { key: 'meta_description', label: 'Meta Desc' },
  { key: 'h1', label: 'H1' },
  { key: 'heading_structure', label: 'Headings' },
  { key: 'keyword_verification', label: 'Verify' },
  { key: 'target_lock', label: 'Targets' }
]

const stepOrder = steps.map((s) => s.key)

const lockedSteps = computed(() => {
  if (!lock.value?.step_statuses) return []
  return stepOrder.filter((s) => lock.value.step_statuses[s] === 'locked')
})

const stepStatus = (key) => lock.value?.step_statuses?.[key] || 'pending'

const stepDotClass = (key) => {
  const status = stepStatus(key)
  if (status === 'locked') return 'bg-primary-500 border-primary-500 text-white'
  if (status === 'active') return 'bg-primary-500 border-primary-500 text-white'
  return 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400'
}

const stepLabelClass = (key) => {
  const status = stepStatus(key)
  if (status === 'locked') return 'text-primary-600 dark:text-primary-400'
  if (status === 'active') return 'text-primary-600 dark:text-primary-400 font-medium'
  return 'text-gray-400 dark:text-gray-500'
}

const stepLineClass = (key) => {
  const status = stepStatus(key)
  if (status === 'locked') return 'bg-primary-500'
  return 'bg-gray-300 dark:bg-gray-600'
}

const stepLabel = (key) => steps.find((s) => s.key === key)?.label || key

const lockedValue = (step) => {
  if (!lock.value) return ''
  switch (step) {
    case 'title_tag': return lock.value.locked_title_tag || ''
    case 'meta_description': return lock.value.locked_meta_description || ''
    case 'h1': return lock.value.locked_h1 || ''
    case 'heading_structure': {
      const headings = lock.value.locked_heading_structure || []
      return headings.map((h) => `H${h.level}: ${h.text}`).join(' | ')
    }
    case 'keyword_verification': {
      const v = lock.value.verification_summary
      return v ? `${v.covered}/${v.total} keywords covered` : 'Verified'
    }
    case 'target_lock': return 'Targets locked'
    default: return ''
  }
}

const customH1ContainsKeyword = computed(() => {
  if (!customH1.value || !lock.value?.generation_context?.primary_keyword) return true
  const kw = lock.value.generation_context.primary_keyword.toLowerCase()
  return customH1.value.toLowerCase().includes(kw)
})

const nestingWarning = computed(() => {
  for (let i = 1; i < editingHeadings.value.length; i++) {
    const prev = editingHeadings.value[i - 1]
    const curr = editingHeadings.value[i]
    if (curr.level > prev.level + 1) {
      return `Heading "${curr.text || '(empty)'}" skips a level (H${prev.level} to H${curr.level}). Consider fixing the nesting.`
    }
  }
  return null
})

const allTargetsIdentical = computed(() => {
  const targets = (lock.value?.locked_targets || []).map((t) => t.target)
  if (targets.length <= 1) return false
  return targets.every((t) => t === targets[0])
})

const allKeywordsSelected = computed(() => {
  const targets = lock.value?.locked_targets || []
  return targets.length > 0 && selectedKeywords.value.size === targets.length
})

const someKeywordsSelected = computed(() => {
  return selectedKeywords.value.size > 0
})

const toggleKeyword = (keyword) => {
  const next = new Set(selectedKeywords.value)
  if (next.has(keyword)) {
    next.delete(keyword)
  } else {
    next.add(keyword)
  }
  selectedKeywords.value = next
}

const toggleAllKeywords = () => {
  if (allKeywordsSelected.value) {
    deselectAllKeywords()
  } else {
    selectAllKeywords()
  }
}

const selectAllKeywords = () => {
  const targets = lock.value?.locked_targets || []
  selectedKeywords.value = new Set(targets.map((t) => t.keyword))
}

const deselectAllKeywords = () => {
  selectedKeywords.value = new Set()
}

const targetCompetitorColumns = computed(() => {
  const targets = lock.value?.locked_targets || []
  if (!targets.length) return []
  const first = targets[0]
  return first.competitor_counts ? Object.keys(first.competitor_counts) : []
})

const charCountClass = (text, limit) => {
  const len = (text || '').length
  if (len > limit) return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
  if (len > limit * 0.9) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
  return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
}

const headingLevelClass = (level) => {
  const map = {
    2: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    3: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    4: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
  }
  return map[level] || map[4]
}

const tierClass = (tier) => {
  const map = {
    primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    secondary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    tertiary: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    supporting: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
  }
  return map[tier] || map.supporting
}

const sortedTargets = computed(() => {
  const targets = lock.value?.locked_targets || []
  return [...targets].sort((a, b) => (b.search_volume ?? -1) - (a.search_volume ?? -1))
})

const formatVolume = (vol) => {
  if (vol == null) return '---'
  if (vol >= 1000) return `${(vol / 1000).toFixed(1)}k`
  return vol.toLocaleString()
}

const formatSource = (source) => {
  const map = {
    competitor_consensus: 'Consensus',
    topic_gap: 'Topic Gap',
    paa: 'PAA',
    semantic: 'Semantic'
  }
  return map[source] || source
}

// Initialize heading editor when reaching step 4
watch(() => lock.value?.current_step, (step) => {
  if (step === 'heading_structure' && lock.value?.heading_structure_recommendation) {
    editingHeadings.value = JSON.parse(JSON.stringify(lock.value.heading_structure_recommendation))
  }
  // Initialize all keywords as selected when reaching target_lock
  if (step === 'target_lock' && lock.value?.locked_targets) {
    selectedKeywords.value = new Set(lock.value.locked_targets.map((t) => t.keyword))
  }
}, { immediate: true })

const addHeading = () => {
  const lastLevel = editingHeadings.value.length ? editingHeadings.value[editingHeadings.value.length - 1].level : 2
  editingHeadings.value.push({ level: Math.min(lastLevel, 4), text: '', keywords: [], source: null })
}

const removeHeading = (index) => {
  editingHeadings.value.splice(index, 1)
}

const indentHeading = (index) => {
  if (editingHeadings.value[index].level < 4) {
    editingHeadings.value[index].level++
  }
}

const outdentHeading = (index) => {
  if (editingHeadings.value[index].level > 2) {
    editingHeadings.value[index].level--
  }
}

const fetchLock = async() => {
  try {
    loading.value = true
    const response = await getHeadingMetaLock(props.pageId)
    lock.value = response.data.heading_meta_lock
    // Auto-populate keyword verification if step 5 has empty data
    if (lock.value?.current_step === 'keyword_verification' &&
        (!lock.value.heading_keyword_verification || lock.value.heading_keyword_verification.length === 0)) {
      try {
        const verifyResponse = await verifyHeadingKeywords(props.pageId)
        updateLock(verifyResponse.data)
      } catch (_e) { /* ignore - user can click Accept manually */ }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching heading meta lock:', e)
  } finally {
    loading.value = false
  }
}

const subscribeToChannel = () => {
  if (subscription) subscription.unsubscribe()
  subscription = consumer.subscriptions.create(
    { channel: 'HeadingMetaLockChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (data.step === 'completed' || data.step === 'failed') {
          stopPolling()
          fetchLock()
        }
      }
    }
  )
}

const handleGenerate = async() => {
  try {
    generating.value = true
    error.value = null
    await generateHeadingMetaOptions(props.pageId)
    subscribeToChannel()
    lock.value = { status: 'pending', current_step: 'title_tag', step_statuses: {} }
    loading.value = false
    startPolling()
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to start generation'
    // eslint-disable-next-line no-console
    console.error('Failed to generate heading meta options:', e)
  } finally {
    generating.value = false
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const response = await getHeadingMetaLock(props.pageId)
      const l = response.data.heading_meta_lock
      if (l && l.status !== 'pending' && l.status !== 'generating') {
        lock.value = l
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const updateLock = (data) => {
  lock.value = data.heading_meta_lock
  error.value = null
}

const handleLockTitle = async() => {
  try {
    locking.value = true
    error.value = null
    const title = selectedTitle.value !== null
      ? lock.value.title_tag_options[selectedTitle.value].text
      : customTitle.value
    if (!title) { error.value = 'Please select or enter a title tag'; return }
    const response = await lockTitleTag(props.pageId, title)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to lock title tag'
  } finally {
    locking.value = false
  }
}

const handleLockMeta = async() => {
  try {
    locking.value = true
    error.value = null
    const desc = selectedMeta.value !== null
      ? lock.value.meta_description_options[selectedMeta.value].text
      : customMeta.value
    if (!desc) { error.value = 'Please select or enter a meta description'; return }
    const response = await lockMetaDescription(props.pageId, desc)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to lock meta description'
  } finally {
    locking.value = false
  }
}

const handleLockH1 = async() => {
  try {
    locking.value = true
    error.value = null
    const h1Val = selectedH1.value !== null
      ? lock.value.h1_options[selectedH1.value].text
      : customH1.value
    if (!h1Val) { error.value = 'Please select or enter an H1'; return }
    const response = await lockH1(props.pageId, h1Val)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to lock H1'
  } finally {
    locking.value = false
  }
}

const handleLockHeadingStructure = async() => {
  try {
    locking.value = true
    error.value = null
    const headings = editingHeadings.value.filter((h) => h.text.trim())
    if (!headings.length) { error.value = 'Please add at least one heading'; return }
    const response = await lockHeadingStructure(props.pageId, headings)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to lock heading structure'
  } finally {
    locking.value = false
  }
}

const handleAcceptVerification = async() => {
  try {
    locking.value = true
    error.value = null
    const response = await verifyHeadingKeywords(props.pageId)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to verify keywords'
  } finally {
    locking.value = false
  }
}

const handleLockTargets = async() => {
  try {
    locking.value = true
    error.value = null
    const response = await lockTargetsApi(props.pageId, [...selectedKeywords.value])
    updateLock(response.data)
    if (response.data.heading_meta_lock?.approved) {
      emit('approved')
    }
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to lock targets'
  } finally {
    locking.value = false
  }
}

const handleUnlock = async(step) => {
  try {
    locking.value = true
    error.value = null
    const response = await unlockStep(props.pageId, step)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to unlock step'
  } finally {
    locking.value = false
  }
}

onMounted(async() => {
  await fetchLock()
  subscribeToChannel()
  // Auto-generate if no lock exists
  if (!lock.value) {
    handleGenerate()
  } else if (lock.value.status === 'pending' || lock.value.status === 'generating') {
    startPolling()
  }
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
